import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * ═══⭐⭐ TURN THE URLS INSIDE A CHAT MESSAGE INTO REAL LINKS ═════════════════
 *
 * Support customers paste links constantly — their project, a broken page, a
 * screenshot host — and in the admin chat those arrived as inert text, so
 * answering meant selecting the URL by hand and pasting it into the address bar.
 *
 * ⚠️⚠️ THIS PIPE BUILDS HTML, SO IT ESCAPES EVERYTHING ITSELF. `bypassSecurityTrustHtml`
 * switches Angular's sanitizer OFF for the value it returns; a message body is text a
 * CUSTOMER wrote, i.e. exactly the input an attacker controls. The text is therefore
 * split into "link" and "not link" runs FIRST and every run is escaped BEFORE any
 * markup is added — never the other way round, which would leave `&amp;` in a query
 * string double-escaped and, far worse, would trust the escaping to catch what the
 * URL pattern already consumed.
 *
 * ⚠️ ONLY `http`, `https` AND BARE `www.` BECOME LINKS. The pattern cannot match
 * `javascript:`, `data:` or any other scheme, which is what keeps a crafted message
 * from producing a dangerous `href` at all rather than relying on a check afterwards.
 *
 * ⚠️ IT IS A PURE PIPE. Angular re-evaluates it only when the string identity changes,
 * so a chat that polls does not re-parse every bubble on every tick.
 */
@Pipe({ name: 'linkify' })
export class LinkifyPipe implements PipeTransform {
  /**
   * A URL run: an explicit scheme, or a bare `www.` host.
   *
   * ⚠️ IT STOPS AT WHITESPACE AND AT THE FOUR CHARACTERS THAT COULD CLOSE THE TAG
   * WE ARE ABOUT TO WRITE (`<`, `>`, `"`, `'`). Those cannot appear unescaped in a
   * URL anyway, and refusing them here means the matched run can never carry markup
   * into the `href` even before escaping runs.
   */
  private static readonly URL_PATTERN = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

  /**
   * Punctuation that ends a SENTENCE far more often than a URL. Trimmed off the tail
   * of a match so "see https://totalum.app." does not link the full stop.
   */
  private static readonly TRAILING_PUNCTUATION = /[.,;:!?]+$/;

  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    const text = value ?? '';
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');

    let html = '';
    let lastIndex = 0;

    // `matchAll` needs the global flag and a fresh lastIndex; the pattern is a shared
    // static, so it is used via `exec` on a per-call regex instead of being mutated.
    const pattern = new RegExp(LinkifyPipe.URL_PATTERN.source, 'gi');
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const raw = match[0];
      const trimmed = LinkifyPipe.trimTail(raw);

      // Everything since the previous link is plain text.
      html += LinkifyPipe.escape(text.slice(lastIndex, match.index));

      if (trimmed) {
        const href = trimmed.toLowerCase().startsWith('www.') ? 'https://' + trimmed : trimmed;
        html +=
          '<a href="' +
          LinkifyPipe.escape(href) +
          // `noopener noreferrer` because the opened page gets a handle on this one
          // otherwise, and the admin panel is a session no customer link should touch.
          '" target="_blank" rel="noopener noreferrer"' +
          // The colour is inherited so the same markup reads correctly on the dark
          // admin bubble and the light customer one; the underline carries the meaning.
          ' class="underline underline-offset-2 break-all hover:opacity-80">' +
          LinkifyPipe.escape(trimmed) +
          '</a>';
      }

      // Punctuation trimmed off the match is text, not part of the link.
      html += LinkifyPipe.escape(raw.slice(trimmed.length));
      lastIndex = match.index + raw.length;
    }

    html += LinkifyPipe.escape(text.slice(lastIndex));

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** Drop sentence punctuation, and a closing bracket that nothing in the URL opened. */
  private static trimTail(url: string): string {
    let out = url.replace(LinkifyPipe.TRAILING_PUNCTUATION, '');

    // "(see https://totalum.app/docs)" — the `)` closes the sentence. But a URL may
    // legitimately contain balanced brackets, so one is only dropped when the URL
    // never opened it.
    while (out.length > 0) {
      const last = out[out.length - 1];
      const opener = last === ')' ? '(' : last === ']' ? '[' : null;
      if (!opener) break;
      if (LinkifyPipe.count(out, opener) >= LinkifyPipe.count(out, last)) break;
      out = out.slice(0, -1).replace(LinkifyPipe.TRAILING_PUNCTUATION, '');
    }

    return out;
  }

  private static count(text: string, char: string): number {
    let n = 0;
    for (const c of text) if (c === char) n++;
    return n;
  }

  /** The only escaping in this file. Covers text nodes and double-quoted attributes. */
  private static escape(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
